import { Box, Button, Divider, Stack, StackProps, Text } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { Trans, useTranslation } from 'react-i18next';
import { ApproveIcon, AttachmentIcon } from '../../../icons';
import { ExtAttachmentType } from '../../../api/extForms';

export type FileDescriptionProps = StackProps & {
  file: FileWithPath;
  error?: string;
  offerFile: ExtAttachmentType;
  onRemove: () => void;
};

/**
 * File description component
 * @private
 *
 * @param file
 * @param error
 * @param offerFile
 * @param onRemove
 * @param stackProps
 */
export const FileDescription: React.FC<FileDescriptionProps> = ({
  file,
  error,
  offerFile,
  onRemove,
  ...stackProps
}) => {
  const { t } = useTranslation();

  return (
    <Stack {...stackProps}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            flex: '0 0 44px',
          }}
          h={44}
          w={44}
          bg="green.1"
          c="green.0"
        >
          <ApproveIcon />
        </Box>
        <Box>
          <Text fz="md" fw={600}>
            <Trans
              i18nKey={
                offerFile.required
                  ? 'common:requiredLabel'
                  : 'common:optionalLabel'
              }
              values={{ label: offerFile.name }}
              components={{ text: <span style={{ fontWeight: 400 }} /> }}
            />
          </Text>
          <Text fz="sm" fw={400}>
            {offerFile.description}
          </Text>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <AttachmentIcon />
        <Stack spacing={4}>
          <Text fz="md" fw={600}>
            {file.name}
          </Text>
          {error ? (
            <Text color="red" fz="sm">
              {t(error)}
            </Text>
          ) : undefined}
        </Stack>
        <Button variant="default" size="sm" onClick={onRemove}>
          {t('common:change')}
        </Button>
      </Box>
      <Divider />
    </Stack>
  );
};
